<script setup lang="ts">
import HeroForm from './HeroForm.vue';
import { useStore } from '~/store';

const formContainer = ref('');
const scrolled = ref(false);
const isMounted = ref(false);
const store = useStore();

const pageConfig = store.$state.pageConfig;
const car = pageConfig?.blocks.find(block => block.__component === 'blocks-car.hero-block-car');
const school = pageConfig?.blocks.find(block => block.__component === 'blocks-school.hero-block-school');
const heroBlock = car || school;
const element = ref();
const isSchoolVersion = !!school;

const checkForm = () => {
  let headerHeight = 480;
  let stickyHeight = 650;

  if (window.innerWidth < 1439) {
    headerHeight = 300;
    stickyHeight = 594;
  }

  scrolled.value = window.scrollY > headerHeight;
  element.value = document.getElementById('main-form');
  formContainer.value = '#main-form';

  if (element.value) {
    if (window.scrollY > stickyHeight) {
      element.value.classList.add('sticky');
    } else {
      element.value.classList.remove('sticky');
    }
  }
};

onMounted(() => {
  isMounted.value = true;
  checkForm();
  window.addEventListener('resize', checkForm);
  window.addEventListener('scroll', checkForm);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkForm);
  window.removeEventListener('scroll', checkForm);
});

</script>

<template>
  <div v-if="pageConfig" class="hero-block" :class="{is_school: isSchoolVersion}" dir="auto">
    <div class="container container--full">
      <div class="hero-block__wrapper">
        <div class="hero-block__start">
          <div class="hero-block__offer is_desktop">
            <div v-if="heroBlock?.badge_text" class="label">
              <img
                v-if="heroBlock?.badge_icon"
                :src="heroBlock?.badge_icon?.url"
                :alt="heroBlock?.badge_text"
                class="label__icon"
              >
              <span>{{ heroBlock.badge_text }}</span>
            </div>
          </div>
          <h1>{{ heroBlock?.title }}</h1>
          <div v-if="heroBlock?.description" class="hero-block__subtitle">
            {{ heroBlock?.description }}
          </div>
        </div>
        <div class="hero-block__end">
          <div class="hero-block__offer is_mobile">
            <div v-if="heroBlock?.badge_text" class="label">
              <img
                v-if="heroBlock?.badge_icon"
                :src="heroBlock.badge_icon.url"
                class="label__icon"
                :alt="heroBlock?.badge_text"
              >
              <span>{{ heroBlock.badge_text }}</span>
            </div>
          </div>
          <div id="hero-form" class="hero-block__form">
            <div class="main__form-wrapper">
              <HeroForm position="top" :content="heroBlock" :is-school-version="isSchoolVersion" />
            </div>
            <Teleport v-if="formContainer" :to="formContainer">
              <transition name="fade-menu">
                <div class="main__form-wrapper">
                  <HeroForm position="scroll" :content="heroBlock" :is-school-version="isSchoolVersion" />
                </div>
              </transition>
            </Teleport>
          </div>
        </div>
        <div class="hero-block__image">
          <picture v-if="heroBlock?.banner_desktop?.url" class="hero-block__image-desk">
            <source :srcset="heroBlock.banner_desktop.url" :type="heroBlock.banner_desktop.mime">
            <img :src="heroBlock.banner_desktop.url" :alt="heroBlock.title">
          </picture>
          <picture v-if="heroBlock?.banner_mobile?.url" class="hero-block__image-mob">
            <source :srcset="heroBlock.banner_mobile.url" :type="heroBlock.banner_mobile.mime">
            <img :src="heroBlock.banner_mobile.url" :alt="heroBlock.title">
          </picture>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/mixins.scss';
@import '@/assets/scss/variables.scss';


.hero-block {
  max-width: 1408px;
  margin: 32px auto 48px;
  position: relative;

  &.is_school {
    .hero-block__wrapper {
      grid-template-columns: 58% 1fr;
      @include sxl {
        grid-template-columns: 575px 1fr;
      }
    }

    .hero-block__image img {
      width: 605px;
      inset-inline-start: calc(50% - 605px / 2 + 146px);
      rotate: -2.6deg;
      bottom: -112px;
      @include sxl {
        inset-inline-start: calc(50% - 605px / 2 + 70px);
      }
      @include lg {
        width: 412px;
        inset-inline-start: calc(50% - 412px / 2 + 220px);
        bottom: 191px;
      }
      @include md {
        width: 300px;
        inset-inline-start: calc(50% - 150px);
        bottom: 190px;
        mask-image: none;
      }
    }

    h1 {
      width: 100%;
      max-width: 100%;
      text-shadow: var(--dimension-0, 0px) var(--dimension-2, 8px) var(--dimension-3, 12px) var(--Alpha-Black-8, rgba(0, 0, 0, 0.08)), var(--dimension-0, 0px) var(--dimension-5, 20px) var(--dimension-6, 24px) var(--Alpha-Black-4, rgba(0, 0, 0, 0.04));
      @include lg {
        max-width: 410px;
      }
    }
    .hero-block__subtitle {
      max-width: 100%;
      @include sxl {
        max-width: 400px;
      }
      @include lg {
        max-width: 375px;
      }
    }
    .hero-block__offer .label {
      border-radius: 8px;
      background: var(--bg-srf-brand-purple, #E3E8FF);
      backdrop-filter: blur(9.5px);
      height: auto;
      span {
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px; /* 150% */
        color: var(--bg-srf-accent-brand, #6568F4);
        background-clip: unset;
        background: none;
        text-fill-color: unset;
        -webkit-text-fill-color: unset;
      }
      &:before {
        display: none;
      }
      &:after {
        background: #E3E8FF;
      }
    }
    .hero-block__offer.is_desktop {
      display: block;
      transform: unset;
      margin: unset;
      margin-bottom: 8px;
      .label {
        position: relative;
        transform: none;
      }
    }
    .hero-block__offer.is_mobile {
      display: none;
    }
  }

  @include md {
    margin-top: 0;
  }
  .container {
    @include md {
      padding: 0;
    }
  }

  &__wrapper {
    display: grid;
    position: relative;
    flex-direction: column;
    grid-template-columns: 1fr 1fr;
    height: 600px;
    align-items: start;
    justify-content: start;
    border-radius: var(--radius-8, 32px);
    background: var(--Rbr-grd-Hero-Blue, linear-gradient(107deg, var(--Accent-Purple-700-Shell-Purple, #6568F4) 0%, var(--Accent-Purple-400, #C5CEFF) 100%));
    padding: var(--spacing-8, 32px) var(--spacing-8, 32px) var(--spacing-10, 32px) var(--spacing-8, 32px);
    width: 100%;
    z-index: 1;

    @include lg {
      padding: var(--spacing-7, 24px);
      display: flex;
      flex-direction: column;
      align-items: stretch;
      height: auto;
    }
    @include md {
      padding: 24px var(--spacing-3, 8px) var(--spacing-3, 8px) var(--spacing-3, 8px);
      border-radius: 0px 0px var(--radius-7, 24px) var(--radius-7, 24px);
      justify-content: start;
      overflow: hidden;
    }

    h1 {
      margin-top: 8px;
      font-style: normal;
      color: var(--txt-srf-contrast-light, #FFF);
      margin-bottom: 8px;
      text-align: start;
      max-width: 475px;

      font-size: 72px;
      font-style: normal;
      font-weight: 600;
      line-height: 80px;
      letter-spacing: -1.44px;

      @include rtl {
        margin-top: 16px;
        margin-bottom: 16px;
        line-height: 92px;
      }

      @include sxl {
        font-size: 48px;
        font-style: normal;
        font-weight: 600;
        line-height: 56px;
        max-width: 310px;
        letter-spacing: 0px;

        @include rtl {
          line-height: 66px;
        }
      }

      @include lg {
        max-width: 430px;
        font-size: 36px;
        font-style: normal;
        font-weight: 600;
        line-height: 44px;
        margin-top: 0;

        @include rtl {
          line-height: 44px;
          margin-top: 0;
        }
      }

      @include md {
        margin-top: 0;
      }

    }
  }

  &__start {
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: start;

    @include lg {
      margin-bottom: var(--spacing-7, 24px);
    }
  }

  &__end {
    display: flex;
    flex-direction: column;
    align-items: end;
    justify-content: start;

    @include md {
      margin-top: 170px;
    }
  }

  &__offer {
    position: relative;
    z-index: 50;

    &.is_desktop {
      display: block;
      @include lg {
        display: none;
      }
    }

    &.is_mobile {
      display: none;
      @include lg {
        display: block;
      }
    }

    @include lg {
      margin: auto;
    }

    .label {
      border-radius: var(--radius-8, 32px);
      background: #FFF;
      backdrop-filter: blur(9.5px);
      display: flex;
      padding: 8px 14px 8px 14px;
      align-items: center;
      gap: var(--spacing-3, 8px);
      background-clip: padding-box;
      height: 42px;
      @include lg {
        position: absolute;
        top: 0;
        width: max-content;
        z-index: 1;
        transform: translate(-50%, -50%);

        @include rtl {
          transform: translate(50%, -50%);
        }

        &:before {
          content: '';
          position: absolute;
          top: 0; right: 0; bottom: 0; left: 0;
          z-index: -1;
          margin: -3.2px;
          border-radius: inherit; /* !importanté */
          background: var(--Rbr-grd-Hero-Blue, linear-gradient(107deg, var(--Accent-Purple-700-Shell-Purple, #6568F4) -20%, var(--Accent-Purple-400, #C5CEFF) 120%));
          clip-path: polygon(0 50%, 100% 50%, 100% 100%, 0% 100%);
        }
        &:after {
          content: '';
          position: absolute;
          top: 0; right: 0; bottom: 0; left: 0;
          z-index: -1;
          border-radius: inherit; /* !importanté */
          background-color: white;
        }
      }

      &__icon {
        width: 24px;
        height: 24px;
      }

      span {
        font-family: "PP Neue Montreal";
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 24px; /* 150% */
        background: linear-gradient(90deg, var(--Maroon-Red-500, #F3405E) -30.22%, var(--bg-srf-accent-brand-hover, #494BB5) 100%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }
  }


  &__subtitle {
    color: var(--txt-srf-contrast-light, #FFF);
    text-align: start;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 36px;
    max-width: 460px;

    @include lg {
      font-size: 20px;
      font-style: normal;
      font-weight: 400;
      line-height: 32px;
      max-width: 400px;
    }

    @include md {
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: 28px;
    }
  }

  &__form {
    position: relative;
    background: #fff;
    border-radius: 24px;
    box-shadow: 0px 5px 20px 0px #210E561A;
    z-index: 10;
    width: 100%;
    max-width: 370px;

    @include sxl {
      max-width: 344px;
    }
    @include lg {
      width: 100%;
      max-width: 100%;
    }
  }

  &__privacy {
    color: #CACAD8;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    margin-top: 68px;
    inset-inline-start: 72px;
    bottom: 48px;
    position: absolute;
    @include lg {
      position: relative;
      margin: auto;
      margin-top: 20px;
      text-align: center;
      inset-inline-start: 0;
      bottom: 0;
    }
    @include md {
      padding: 8px 0;
      margin-top: 16px;
      font-size: 12px;
    }
    & ::v-deep a {
      font-family: inherit;
      color: #CACAD8;
      text-decoration: underline;
      &:hover {
        text-decoration: none;
      }
    }
  }

  .main__form {
    &-wrapper {
      padding: 24px;
      @include md {
        padding: var(--spacing-5, 16px);
      }
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    overflow: hidden;
    border-radius: 48px;
    z-index: -1;

    img {
      $width: 710px;
      position: absolute;
      inset-inline-start: calc(50% - #{$width}/2 + 0px);
      bottom: -190px;
      margin: auto;
      width: $width;

      transition: transform 0.2s linear;
      position: absolute;
      animation: moveGlobe 1s ease forwards;
      @include sxl {
        $width: 603px;
        width: $width;
        inset-inline-start: calc(50% - #{$width}/2 + 0px);
        bottom: -170px;
      }
      @include lg {
        $width: 370px;
        width: $width;
        inset-inline-start: calc(79% - 370px / 2 + 0px);
        top: 0;
        bottom: auto;
      }
      @include md {
        $width: 370px;
        max-width: $width;
        top: auto;
        bottom: 130px;
        inset-inline-start: calc(50% - #{$width}/2 + 0px);
        mask-image: linear-gradient(180deg, rgba(255, 255, 255, 0) -30%, rgba(255, 255, 255, 0) -30%, rgb(0, 0, 0) 50%)
      }
    }

    &-desk img {
      display: block;
      @include lg {
        display: none;
      }
    }
    &-mob img {
      display: none;
      @include lg {
        display: block;
      }
    }
  }
}

@keyframes moveGlobe {
  0% {
    transform: translateY(-30%) rotate(0deg);
    opacity: 0;
  }
  100% {
    opacity: 1;
    transform: translateY(0%) rotate(2.6deg);
  }
}

@keyframes moveGlobe-mobile {
  0% {
    opacity: 0;
    transform: scale(0.8) translateX(50%);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateX(50%);
  }
}

@keyframes moveGlobe-mobile-rtl {
  0% {
    opacity: 0;
    transform: scale(0.8) translateX(-50%);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateX(-50%);
  }
}
</style>
